import React from "react";
import { useNavigate } from "react-router-dom";
import {
  routesForAuthenticatedOnly,
  routesForNotAuthenticatedOnly,
} from "../../../app/routes";
import useRoutePathMatch from "@jumbo/hooks/useRoutePathMatch";
import { removeToken, storeToken } from "./authHelpers";
import { config } from "../../../app/config/main";
import { AuthContext } from "@jumbo/components/JumboAuthProvider/JumboAuthContext";
import API from "app/services/api";

const init = () => {
  let authUser = null;
  const storedToken = localStorage.getItem("token");

  if (!config?.authSetting) {
    throw Error(
      `You are using JumboAuthProvider but you haven't setup authSetting inside /src/app/config/main.js's config object`
    );
  }

  if (storedToken) {
    try {
      const tokenParsed = JSON.parse(atob(storedToken.split('.')[1]));
      if (tokenParsed.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        return {
          authToken: null,
          authUser: null,
          isLoading: false,
          fallbackPath: config.authSetting.fallbackPath,
        };
      }
      storeToken(storedToken);
    } catch (error) {
      localStorage.removeItem('token');
      return {
        authToken: null,
        authUser: null,
        isLoading: false,
        fallbackPath: config.authSetting.fallbackPath,
      };
    }
  }

  return {
    authToken: storedToken ?? null,
    authUser: authUser,
    isLoading: !!storedToken, // Se tem token, começar carregando para buscar user
    fallbackPath: config.authSetting.fallbackPath,
  };
};

const checkAuthStatus = async () => {
  try {
    const response = await API.get("/auth/status", {
      withCredentials: true,
    });

    if (!response.data || !response.data.user) {
      return null;
    }

    return response.data;
  } catch (error) {
    return null;
  }
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "set-auth-data":
      return {
        ...state,
        ...action.payload,
      };

    case "start-loading":
      return {
        ...state,
        isLoading: true,
      };

    case "stop-loading":
      return {
        ...state,
        isLoading: false,
      };
    default:
      return state;
  }
};

const JumboAuthProvider = ({ children, ...restProps }) => {
  const [authOptions, setAuthOptions] = React.useReducer(
    authReducer,
    restProps,
    init
  );
  const [logout, setLogout] = React.useState(false);
  const navigate = useNavigate();
  const isAuthenticatedRouteOnly = useRoutePathMatch(
    routesForAuthenticatedOnly
  );
  const isNotAuthenticatedRouteOnly = useRoutePathMatch(
    routesForNotAuthenticatedOnly
  );

  React.useEffect(() => {
    if (logout) {
      removeToken();
      localStorage.clear();
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: null, authUser: null, isLoading: false },
      });
      setLogout(false);
    }
  }, [logout]);

  const setAuthToken = React.useCallback(async (token) => {
    setAuthOptions({ type: "start-loading" });

    if (!token) {
      setLogout(true);
      return;
    }

    storeToken(token);
    try {
      const authUser = await checkAuthStatus();

      if (authUser && authUser.user) {
        setAuthOptions({
          type: "set-auth-data",
          payload: {
            authToken: token,
            authUser: authUser.user,
            isLoading: false,
          },
        });
        return;
      }

      // checkAuthStatus falhou - limpar token e parar de carregar
      localStorage.removeItem('token');
      removeToken();
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: null, authUser: null, isLoading: false },
      });
    } catch (error) {
      localStorage.removeItem('token');
      removeToken();
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: null, authUser: null, isLoading: false },
      });
    }
  }, []);

  const setRedirectPath = React.useCallback((redirectPath) => {
    setAuthOptions({ type: "set-redirect-path", payload: { redirectPath } });
  }, []);

  const setAuthData = React.useCallback((data) => {
    setAuthOptions({ type: "set-auth-data", payload: data });
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      ...authOptions,
      setAuthData,
      setRedirectPath,
      setAuthToken,
      setAuthOptions,
    };
  }, [authOptions, setAuthData, setRedirectPath, setAuthToken, setAuthOptions]);

  // Efeito para restaurar sessão no carregamento da página (token no localStorage mas sem authUser)
  const hasTriedRestore = React.useRef(false);
  React.useEffect(() => {
    if (authOptions?.authToken && !authOptions?.authUser && !hasTriedRestore.current) {
      hasTriedRestore.current = true;
      setAuthToken(authOptions.authToken);
    }
  }, [authOptions?.authToken, authOptions?.authUser, setAuthToken]);

  // Efeito para redirecionar usuário autenticado para fora de rotas de login
  React.useEffect(() => {
    if (authOptions?.isLoading) {
      return;
    }

    if (authOptions?.authUser && isNotAuthenticatedRouteOnly) {
      navigate(config.authSetting.redirectNotAuthenticatedPath ?? "/dashboard");
    }
  }, [
    authOptions?.authUser,
    authOptions?.isLoading,
    isNotAuthenticatedRouteOnly,
    navigate,
  ]);

  // Efeito para redirecionar para login se tentar acessar rota protegida sem auth
  React.useEffect(() => {
    if (authOptions?.isLoading) {
      return;
    }
    if (isAuthenticatedRouteOnly && !authOptions?.authUser) {
      navigate('/login');
    }
  }, [authOptions?.authUser, authOptions?.isLoading, isAuthenticatedRouteOnly, navigate]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default JumboAuthProvider;
